import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { useUpsertDatasheetType, type DatasheetType, type UpsertDatasheetTypeBody } from "@/lib/datasheets";

// The backend rejects any work-queue code that isn't one of the valid task types,
// so present them as a closed dropdown rather than free text.
const WORK_QUEUE_CODES = ["", "Admin", "Badge", "Production", "Artwork"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: DatasheetType | null;
}

export function DatasheetTypeDialog({ open, onOpenChange, existing }: Props) {
  const [code, setCode] = useState(existing?.datasheetTypeCode ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [productTypeCode, setProductTypeCode] = useState(existing?.productTypeCode ?? "");
  const [jobStatus, setJobStatus] = useState(existing?.defaultJobStatusCode ?? "");
  const [workQueue, setWorkQueue] = useState(existing?.addOrderToWorkQueueCode ?? "");
  const [saving, setSaving] = useState(false);
  const upsert = useUpsertDatasheetType();
  const canSave = code.trim() !== "" && description.trim() !== "" && !saving;

  async function save() {
    setSaving(true);
    try {
      const body: UpsertDatasheetTypeBody = {
        description: description.trim(),
        productTypeCode: productTypeCode || null,
        defaultJobStatusCode: jobStatus || null,
        addOrderToWorkQueueCode: workQueue || null,
        assignTaskToEmployeeId: existing?.assignTaskToEmployeeId ?? null,
      };
      await upsert.mutateAsync({ code: code.trim(), body });
      toast.success("Type saved");
      onOpenChange(false);
    } catch (e) {
      const detail = e instanceof ApiError ? (e.body as { detail?: string } | null)?.detail : null;
      toast.error(detail ?? "Could not save the type");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{existing ? "Edit datasheet type" : "New datasheet type"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label htmlFor="dt-code">Code</Label>
            <Input id="dt-code" value={code} disabled={existing !== null} onChange={(e) => setCode(e.target.value.toUpperCase())} /></div>
          <div className="space-y-1"><Label htmlFor="dt-desc">Description</Label>
            <Input id="dt-desc" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="dt-prod">Product type</Label>
            <Input id="dt-prod" value={productTypeCode} onChange={(e) => setProductTypeCode(e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="dt-status">Default job status</Label>
            <Input id="dt-status" value={jobStatus} onChange={(e) => setJobStatus(e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="dt-wq">Add order to work queue</Label>
            <select id="dt-wq" aria-label="Add order to work queue" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={workQueue} onChange={(e) => setWorkQueue(e.target.value)}>
              {WORK_QUEUE_CODES.map((c) => <option key={c} value={c}>{c === "" ? "None" : c}</option>)}
            </select></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => void save()} disabled={!canSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
